import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { SelectDataType } from 'components/Rsuite/Select';
import { DefaultBrandManager } from 'core/brand/BrandManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { useCampaignStore } from 'store/campaignStore';
import { useOrderStore } from 'store/orderStore';
import { ActionType, BudgetType } from 'core/rtbCampaign/singlePage/common';
import moment from 'moment';
import useCreativeStore from 'store/creativeStore';
import { useProductStore } from 'store/productStore';

interface ShopFormProps {
  defaultForm: any;
  values: ReturnType<typeof useFormik>['values'];
  setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
}

interface ExtendedSelectDataType extends SelectDataType {
  value: string;
  label: string;
  floorCPC?: number;
  ceilingCPC?: number;
  floorDailyBudget?: number;
  ceilingDailyBudget?: number;
}
const defaultBrandManager = new DefaultBrandManager();

const useShopForm = (
  initialState: ShopFormProps = {
    defaultForm: {},
    values: {},
    setFieldValue: async (
      field: string,
      value: any,
      shouldValidate?: boolean,
    ) => Promise.resolve(),
  },
) => {
  const { defaultForm, values, setFieldValue } = initialState;
  const { callAPIs } = useCallAPI();
  const { order } = useOrderStore();
  const { handleChangeCreative } = useCreativeStore();
  const { resetProductStore } = useProductStore();
  // actionType => 新增(create) or 修改(edit)
  const { loading, handleLoading, handleSelectBrand, adType, actionType } =
    useCampaignStore();

  // 品牌選擇下拉選單
  const [brandOptions, setBrandOptions] = useState<ExtendedSelectDataType[]>(
    [],
  );

  // 黃金置頂建議出價
  const [suggestCpc, setSuggestCpc] = useState<number[]>([]);
  const [suggestDailyTargetBudget, setSuggestDailyTargetBudget] = useState<
    number[]
  >([]);
  const [diffDays, setDiffDays] = useState(1);

  // 取得品牌選項
  const fetchBrandOptions = async (): Promise<void> => {
    try {
      handleLoading(true);
      await callAPIs(
        [defaultBrandManager.getBrandOptions.bind(defaultBrandManager)],
        (brandOptions) => {
          // 必須過濾掉"其他"品牌
          const preprocessBrandOptions = brandOptions.filter(
            ({ value }) => value !== '99999999999999999',
          );
          setBrandOptions(preprocessBrandOptions);
          handleLoading(false);
        },
      );
    } catch (error) {
      handleLoading(false);
      console.error('Failed to fetch data:', error);
    }
  };
  // 切換品牌
  const handleChangeBrand = (webBrandNo) => {
    const brandName = brandOptions.filter(
      (item) => item.value === webBrandNo,
    )[0]?.label;
    setFieldValue('basic.webBrandNo', webBrandNo);
    setFieldValue('basic.brandName', brandName);
    handleSetSuggestCpc(webBrandNo);
    handleSelectBrand({ value: webBrandNo, label: brandName });
    handleChangeCreative({});
    resetProductStore();
  };
  // 變更活動區間
  const handleDateRangeChange = (startDate, endDate) => {
    setFieldValue('basic.startDate', startDate);
    setFieldValue('basic.endDate', endDate);
  };

  const handleChangeStartDate = (startDate) => {
    setFieldValue('basic.startDate', startDate);
  };
  const handleChangeEndDate = (endDate) => {
    setFieldValue('basic.endDate', endDate);
  };

  // 變更預算類型
  const handleBudgetTypeChange = (newBudgetType) => {
    setFieldValue('basic.budgetType', newBudgetType);
    handleResetAllBudget();
  };
  // 變更預算
  const handleBudgetChange = (newBudget) => {
    const { type, newPrice } = newBudget;
    if (type === BudgetType.DAILY) {
      setFieldValue('basic.dailyTargetBudget', newPrice);
      // 編輯模式下日預算模式有異動日預算才執行 handleAutoSetBudget
      if (
        actionType === ActionType.EDIT &&
        Number(newPrice) === defaultForm.basic.dailyTargetBudget
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAutoSetBudget(newPrice);
      }
    } else if (type === BudgetType.TOTAL) {
      setFieldValue('basic.budget', newPrice);
      handleAutoSetDailyBudget(newPrice);
    }
  };
  // 自動設定每日預算
  const handleAutoSetDailyBudget = (newBudget) => {
    if (values.basic.budgetType === BudgetType.TOTAL) {
      setFieldValue('basic.dailyTargetBudget', Number(newBudget).toFixed(2));
    }
  };
  // 自動設定總預算
  const handleAutoSetBudget = (newDailyBudget) => {
    const { basic } = values;
    const start = moment(basic.startDate);
    const end = moment(basic.endDate);
    const today = moment();

    // 根據狀態選擇計算天數的基準
    const baseDate =
      actionType === (ActionType.CREATE || ActionType.COPY)
        ? start
        : start.isAfter(today)
          ? start
          : today;

    // +1 是因為同日也算 1 天
    const days = end.diff(baseDate, 'days') + 1;

    if (basic.budgetType === BudgetType.DAILY) {
      const daily = Number(newDailyBudget) || 0;
      const total = basic.expectedSpent + daily * days;
      // 四捨五入到小數第二位，並用字串表示
      const formatted = (
        Math.round((total + Number.EPSILON) * 100) / 100
      ).toFixed(2);
      setFieldValue('basic.budget', formatted);
    }
  };
  // 重置所有預算
  const handleResetAllBudget = () => {
    setFieldValue('basic.budget', '0');
    setFieldValue('basic.dailyTargetBudget', '0');
  };
  // 根據選擇的品牌取得建議出價
  const handleSetSuggestCpc = (brand: string) => {
    const selectBrand = brandOptions.find((option) => option.value === brand);
    setSuggestCpc([selectBrand?.floorCPC || 0, selectBrand?.ceilingCPC || 0]);
    setSuggestDailyTargetBudget([
      selectBrand?.floorDailyBudget || 0,
      selectBrand?.ceilingDailyBudget || 0,
    ]);
  };
  // 變更出價上限
  const handleChangeBidPrice = (newBidPrice) => {
    setFieldValue('basic.bidPrice', newBidPrice);
  };

  useEffect(() => {
    const startDate = moment(values?.basic.startDate);
    const endDate = moment(values?.basic.endDate);
    const defaultStartDate = moment(defaultForm?.basic.startDate);
    const defaultEndDate = moment(defaultForm?.basic.endDate);
    // 需要 +1 是因為當 startDate & endDate 都是同一天時 moment.js 會判定 diff 為 0,
    // 但這邊業務需求會需要是1天
    const diffDays = endDate.diff(startDate, 'days') + 1;
    setDiffDays(diffDays);
    // 編輯模式下日預算模式有異動開始與結束日期才執行 handleAutoSetBudget
    if (values.basic.budgetType === BudgetType.DAILY) {
      if (
        actionType === ActionType.EDIT &&
        startDate.isSame(defaultStartDate) &&
        endDate.isSame(defaultEndDate) &&
        defaultForm.basic.dailyTargetBudget?.toString() ===
          values.basic.dailyTargetBudget?.toString()
      ) {
        setFieldValue('basic.budget', defaultForm.basic.budget);
      } else {
        handleAutoSetBudget(values.basic.dailyTargetBudget);
      }
    }
  }, [
    values.basic.budgetType,
    values.basic.startDate,
    values.basic.endDate,
    values.basic.dailyTargetBudget,
  ]);

  useEffect(() => {
    if (defaultForm.basic.webBrandNo) {
      handleSetSuggestCpc(values.basic.webBrandNo);
    }
    if (actionType === ActionType.COPY) {
      handleChangeBrand(defaultForm.basic.webBrandNo);
    }
  }, [brandOptions, defaultForm.basic.webBrandNo]);

  // 第一次渲染取得品牌下拉選單
  useEffect(() => {
    fetchBrandOptions();
  }, []);

  return {
    order,
    brandOptions,
    handleLoading,
    adType,
    actionType,
    loading,
    diffDays,
    suggestCpc,
    suggestDailyTargetBudget,
    handleChangeBrand,
    handleDateRangeChange,
    handleChangeStartDate,
    handleChangeEndDate,
    handleAutoSetBudget,
    handleSetSuggestCpc,
    handleBudgetTypeChange,
    handleBudgetChange,
    handleChangeBidPrice,
  };
};

export default useShopForm;
